import React from 'react';
import { caseStudies } from '../case-studies/caseStudiesData';
import { TrendingUp, Quote } from 'lucide-react';

const proofSlugs = ['fishbuddy', 'skida'];

const Proof = () => {
  const proofs = caseStudies.filter((c) => proofSlugs.includes(c.slug));

  return (
    <section
      id="app-growth-proof"
      data-testid="app-growth-proof"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <div className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold uppercase tracking-wider mb-4">
            Real work, real numbers
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
            Proof matters. Especially when{' '}
            <span className="text-blue-600">growth budgets</span> are on the line.
          </h2>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Before app companies trust a growth partner, they need to see relevant thinking,
            measurable work, and signs that the team understands mobile performance.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {proofs.map((s) => (
            <article
              key={s.slug}
              data-testid={`proof-${s.slug}`}
              className="bg-gradient-to-br from-white to-gray-50 border border-gray-100 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-24 h-14 bg-white border border-gray-100 rounded-xl flex items-center justify-center p-2">
                  <img
                    src={s.logo}
                    alt={`${s.client} logo`}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-blue-600 font-semibold">
                    {s.category} · {s.duration}
                  </div>
                  <div className="text-sm text-gray-500">{s.client}</div>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-5 leading-snug">
                {s.title}
              </h3>

              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="text-blue-600" size={16} />
                <span className="text-sm font-semibold text-gray-900">Highlights</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {s.results.slice(0, 6).map((r, i) => (
                  <div
                    key={i}
                    className="bg-white border border-gray-100 rounded-xl p-3"
                  >
                    <div className="text-lg font-bold text-blue-600 leading-none">
                      {r.value}
                    </div>
                    <div className="text-xs text-gray-600 mt-1 leading-tight">{r.label}</div>
                  </div>
                ))}
              </div>

              <div className="border-l-4 border-blue-600 bg-blue-50/40 rounded-r-xl p-4 mb-5">
                <Quote className="text-blue-600 mb-1" size={16} />
                <p className="text-sm text-gray-700 italic leading-relaxed">{s.takeaway}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                {s.services.slice(0, 5).map((sv, i) => (
                  <span
                    key={i}
                    className="text-xs px-2.5 py-1 rounded-full bg-white border border-blue-200 text-blue-700"
                  >
                    {sv}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="/case-studies"
            className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-600 font-semibold text-sm group"
          >
            See all case studies
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Proof;
